/**
 * MyWorkLogs.js
 *
 * Work Accomplishments Log - Maintenance + Closed Complaints
 * For Technicians - displays all their completed work activities in one place
 *
 * Features:
 * - View all completed maintenance tasks and resolved (closed) complaints
 * - Quick filter presets (All / Last 7d / Last 15d / Last 30d / Complaints Only / Maintenance Only)
 * - Advanced filters drawer (date range, department, maintenance type, complaint status, severity)
 * - Search by complaint ID, device ID, device name, description
 * - View summary statistics (complaint count, maintenance count, total hours)
 * - Expandable row details for full work information with severity, resolution details
 * - Pagination with configurable items per page
 * - Clean, minimal table design (only essential columns)
 *
 * Note: Shows closed complaints by default; cancelled complaints appear when filtered via Complaint Status
 * Status column removed as all entries represent completed work
 *
 * Props:
 * - token: JWT authentication token
 * - user: Current user object
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { DeviceIDBadge, ComplaintIDBadge, SeverityBadge } from './Badges';
import StatsCard from './StatsCard';
import MoreFiltersButton from './MoreFiltersButton';
import Pagination from './Pagination';
import ClampedDetailsText from './ClampedDetailsText';
import ExportButton from '../modals/ExportButton';
import ExportColumnConfigurationModal from '../modals/ExportColumnConfigurationModal';
import { formatHoursSpent } from '../../../utils/numberUtils';
import { exportFilteredMyWorkLogs } from '../../../utils/exportUtils';
import {
  createMaintenanceExportColumnsState,
  getSelectedExportColumnHeaders,
} from '../../../features/devices/constants/maintenanceExportColumns';
import {
  PDF_EXPORT_MAX_COLUMNS,
  validatePdfExportColumnCount,
} from '../../../constants/exportColumnPolicy';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import {
  ADVANCED_FILTER_HINTS,
  FilterTitleHint,
} from './advancedFilterDrawerHints';
import { MAINTENANCE_LIST_DEFAULT_LOOKBACK_DAYS } from '../../utils/listDefaults';
import './MyWorkLogs.css';

const toDateString = (date) => date.toISOString().split('T')[0];

const daysAgoDateString = (days) => {
  const today = new Date();
  return toDateString(new Date(today.getTime() - days * 24 * 60 * 60 * 1000));
};

const EMPTY_FILTERS = {
  dateFrom: '',
  dateTo: '',
  typeFilter: '',
  departmentFilters: [],
  maintenanceTypeFilters: [],
  complaintStatusFilters: [],
  severityFilters: [],
};

const MyWorkLogs = ({ token, user }) => {
  const [workLogs, setWorkLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [drawerFilters, setDrawerFilters] = useState({ ...EMPTY_FILTERS });
  const [showAdvancedPanel, setShowAdvancedPanel] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    dateRange: true,
    department: true,
    maintenanceType: false,
    complaintFilters: false,
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Search input state - separate from filter state to prevent focus loss
  // searchInputValue is updated immediately as user types
  // searchTerm is updated with debounce (500ms delay) for API calls
  const [searchInputValue, setSearchInputValue] = useState('');
  const searchDebounceTimerRef = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Sorting state
  const [sorting, setSorting] = useState({
    sort_by: 'date',
    sort_direction: 'desc'
  });

  // Data
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportColumnsConfig, setShowExportColumnsConfig] = useState(false);
  const [exportColumns, setExportColumns] = useState(() => createMaintenanceExportColumnsState('excel'));
  const [stats, setStats] = useState({
    total_count: 0,
    maintenance_count: 0,
    complaint_count: 0,
    total_hours: 0,
    maintenance_hours: 0,
    complaint_hours: 0
  });

  const assignedDepartments = user?.departments || [];
  const isDepartmentFilterRestricted = assignedDepartments.length <= 1;
  const drawerHasComplaintFilters = drawerFilters.complaintStatusFilters.length > 0
    || drawerFilters.severityFilters.length > 0;
  const drawerHasMaintenanceTypeFilters = drawerFilters.maintenanceTypeFilters.length > 0;

  const appendFilterParams = (params) => {
    if (filters.typeFilter) {
      params.type = filters.typeFilter;
    }
    if (filters.dateFrom) {
      params.date_from = filters.dateFrom;
    }
    if (filters.dateTo) {
      params.date_to = filters.dateTo;
    }
    if (filters.departmentFilters.length > 0) {
      params.departments = filters.departmentFilters.join(',');
    }
    if (filters.maintenanceTypeFilters.length > 0) {
      params.maintenance_types = filters.maintenanceTypeFilters.join(',');
    }
    if (filters.complaintStatusFilters.length > 0) {
      params.complaint_statuses = filters.complaintStatusFilters.join(',');
    }
    if (filters.severityFilters.length > 0) {
      params.severities = filters.severityFilters.join(',');
    }
  };

  useEffect(() => {
    fetchWorkLogs();
  }, [token, currentPage, perPage, searchTerm, sorting, filters]);

  // ═══════════════════════════════════════════════════════════════════
  // DEBOUNCE SEARCH INPUT - Prevent focus loss while typing
  // ═══════════════════════════════════════════════════════════════════
  // How it works:
  // 1. User types → setSearchInputValue (instant, keeps focus)
  // 2. Debounce waits 500ms after last keystroke
  // 3. Updates searchTerm → triggers API call
  // 4. Component re-renders but input stays focused (it's bound to searchInputValue, not searchTerm)
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Clear any pending debounce timer
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    // If user cleared the search, update immediately
    if (searchInputValue === '') {
      setSearchTerm('');
      setCurrentPage(1);
      return;
    }

    // Set new debounce timer for non-empty searches
    searchDebounceTimerRef.current = setTimeout(() => {
      setSearchTerm(searchInputValue);
      setCurrentPage(1);
    }, 500); // 500ms debounce delay

    // Cleanup on unmount
    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInputValue]);

  // Sync searchInputValue when searchTerm is cleared externally
  useEffect(() => {
    if (searchTerm === '' && searchInputValue !== '') {
      setSearchInputValue('');
    }
  }, [searchTerm]);

  const fetchWorkLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      let params = {
        page: currentPage,
        limit: perPage
      };

      // Add search filter
      if (searchTerm.trim()) {
        params.search = searchTerm;
      }

      // Add filters
      appendFilterParams(params);

      // Add sorting
      if (sorting && sorting.sort_by) {
        params.sort_by = sorting.sort_by;
        params.sort_direction = sorting.sort_direction;
      }

      const response = await axios.get(API_ENDPOINTS.MAINTENANCE.MY_LOGS, {
        params,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setStats({
        total_count: response.data.total_count || 0,
        maintenance_count: response.data.maintenance_count || 0,
        complaint_count: response.data.complaint_count || 0,
        total_hours: response.data.total_hours || 0,
        maintenance_hours: response.data.maintenance_hours || 0,
        complaint_hours: response.data.complaint_hours || 0
      });

      // Set pagination metadata from server response
      setTotalRecords(response.data.total_count || 0);
      setTotalPages(Math.ceil((response.data.total_count || 0) / perPage));

      // Process the paginated work logs
      const processedLogs = (response.data.logs || []).map(log => {
        if (log.type === 'maintenance') {
          return {
            id: `maintenance-${log.id}`,
            type: 'maintenance',
            title: `${log.maintenance_type} - ${log.device_name}`,
            date: log.updated_at,
            logDate: log.created_at,
            hours_spent: log.hours_spent || 0,
            status: 'completed',
            department: log.department,
            department_icon: log.department_icon || '🏢',
            device_id: log.device_identifier,
            device_name: log.device_name,
            maintenance_type: log.maintenance_type,
            description: log.description,
            maintenance_date: log.maintenance_date,
            due_date: log.due_date,
            next_due_date: log.next_due_date,
            icon: getMaintenanceIcon(log.maintenance_type)
          };
        } else {
          // Complaint
          return {
            id: `complaint-${log.id}`,
            type: 'complaint',
            title: log.title,
            date: log.updated_at,
            logDate: log.created_at,
            hours_spent: log.hours_spent || 0,
            status: log.status,
            severity: log.severity || 'minor',
            department: log.department,
            department_icon: log.department_icon || '🏢',
            device_id: log.device_identifier,
            device_name: log.device_name,
            complaint_id: log.complaint_identifier,
            problem_details: log.problem_details,
            closing_remarks: log.closing_remarks,
            spare_parts_required: log.spare_parts_required,
            created_at: log.created_at,
            closed_at: log.closed_at,
            due_date: log.due_date,
            icon: '💬'
          };
        }
      });

      setWorkLogs(processedLogs);
    } catch (err) {
      console.error('Error fetching work logs:', err);
      setError('Failed to load work logs');
      toast.error('Failed to load work logs');
    } finally {
      setLoading(false);
    }
  };


  const getMaintenanceIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'ppm':
        return '🔧';
      case 'calibration':
        return '⚙️';
      case 'compliance':
        return '📋';
      default:
        return '📝';
    }
  };

  /**
   * Handle column sort change - toggle direction if same column, or set new column with asc
   */
  const handleSortChange = (column) => {
    setCurrentPage(1);
    setExpandedRowId(null);
    setSorting(prev => {
      if (prev.sort_by === column) {
        return {
          ...prev,
          sort_direction: prev.sort_direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return {
        sort_by: column,
        sort_direction: 'asc'
      };
    });
  };

  const applyFilters = (nextFilters) => {
    setFilters(nextFilters);
    setCurrentPage(1);
    setExpandedRowId(null);
  };

  const clearAllFilters = () => {
    applyFilters({ ...EMPTY_FILTERS });
  };

  const openAdvancedPanel = () => {
    setDrawerFilters({ ...filters });
    setShowAdvancedPanel(true);
  };

  const applyDrawerFilters = () => {
    if (drawerFilters.dateFrom && drawerFilters.dateTo && drawerFilters.dateFrom > drawerFilters.dateTo) {
      toast.error('From date cannot be greater than To date');
      return;
    }

    const drawerHasComplaintFilters = drawerFilters.complaintStatusFilters.length > 0
      || drawerFilters.severityFilters.length > 0;
    if (drawerFilters.maintenanceTypeFilters.length > 0 && drawerHasComplaintFilters) {
      toast.error('Choose either Maintenance Type or Complaint filters, not both.');
      return;
    }

    let typeFilter = drawerFilters.typeFilter;
    if (drawerFilters.maintenanceTypeFilters.length > 0) {
      typeFilter = 'maintenance';
    } else if (drawerHasComplaintFilters) {
      typeFilter = 'complaint';
    }

    applyFilters({
      ...drawerFilters,
      typeFilter,
    });
    setShowAdvancedPanel(false);
  };

  const isLastNDaysActive = (days) => {
    if (!filters.dateFrom || !filters.dateTo) return false;
    return (
      filters.dateFrom === daysAgoDateString(days)
      && filters.dateTo === toDateString(new Date())
    );
  };

  const toggleLastNDays = (days) => {
    if (isLastNDaysActive(days)) {
      applyFilters({ ...filters, dateFrom: '', dateTo: '' });
      return;
    }
    applyFilters({
      ...filters,
      dateFrom: daysAgoDateString(days),
      dateTo: toDateString(new Date()),
    });
  };

  const isServerDateWindowActive = !filters.dateFrom && !filters.dateTo;

  const toggleTypeFilter = (type) => {
    applyFilters({
      ...filters,
      typeFilter: filters.typeFilter === type ? '' : type
    });
  };

  const buildExportParams = () => {
    const params = {};
    if (searchTerm.trim()) {
      params.search = searchTerm.trim();
    }
    appendFilterParams(params);
    if (sorting?.sort_by) {
      params.sort_by = sorting.sort_by;
      params.sort_direction = sorting.sort_direction;
    }
    return params;
  };

  const exportToPDF = async () => {
    if (!token) return;
    const selected = getSelectedExportColumnHeaders(exportColumns);
    if (selected.length === 0) {
      toast.error('Select at least one column to export');
      return;
    }
    const pdfCheck = validatePdfExportColumnCount(selected.length);
    if (!pdfCheck.ok) {
      toast.error(pdfCheck.message);
      return;
    }
    setIsExporting(true);
    try {
      const { exportedRowCount } = await exportFilteredMyWorkLogs(
        buildExportParams(),
        'pdf',
        token,
        selected
      );
      const count = exportedRowCount ?? totalRecords;
      toast.success(
        count > 0
          ? `Exported ${count} record${count !== 1 ? 's' : ''} to PDF`
          : 'Export complete'
      );
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(error.message || 'Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const exportToExcel = async () => {
    if (!token) return;
    const selected = getSelectedExportColumnHeaders(exportColumns);
    if (selected.length === 0) {
      toast.error('Select at least one column to export');
      return;
    }
    setIsExporting(true);
    try {
      const { exportedRowCount } = await exportFilteredMyWorkLogs(
        buildExportParams(),
        'excel',
        token,
        selected
      );
      const count = exportedRowCount ?? totalRecords;
      toast.success(
        count > 0
          ? `Exported ${count} record${count !== 1 ? 's' : ''} to Excel`
          : 'Export complete'
      );
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error(error.message || 'Failed to export Excel');
    } finally {
      setIsExporting(false);
    }
  };

  const handleClearSearch = () => {
    setSearchInputValue('');
    setSearchTerm('');
    setCurrentPage(1);
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }
  };

  /**
   * Get sort indicator for column header (▲ for asc, ▼ for desc)
   */
  const getSortIndicator = (column) => {
    if (sorting.sort_by !== column) return '';
    return sorting.sort_direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Filtering, sorting, and pagination are now handled server-side
  // workLogs contains the current page's filtered/sorted results

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  // Calculate resolution time duration for complaints (human-readable format)
  const calculateResolutionTime = (createdAt, resolvedAt) => {
    if (!createdAt || !resolvedAt) return 'N/A';

    const created = new Date(createdAt);
    const resolved = new Date(resolvedAt);
    const diffMs = resolved - created;

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ${hours}h`;
    }
    return `${hours}h`;
  };

  // Determine if maintenance was completed on-time based on due date
  // Compares actual maintenance_date against the due_date (SLA from devices table)
  const getMaintenanceTimeliness = (maintenanceDate, dueDate) => {
    if (!maintenanceDate || !dueDate) return { isOnTime: null, daysLate: 0 };

    const completed = new Date(maintenanceDate);
    const due = new Date(dueDate);
    const isOnTime = completed <= due;
    const diffMs = completed - due;
    const daysLate = isOnTime ? 0 : Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    return {
      isOnTime,
      daysLate
    };
  };

  // Active quick/advanced-style filters (search handled separately)
  const hasQuickFiltersActive = Boolean(
    filters.dateFrom
    || filters.dateTo
    || filters.typeFilter
    || filters.departmentFilters.length > 0
    || filters.maintenanceTypeFilters.length > 0
    || filters.complaintStatusFilters.length > 0
    || filters.severityFilters.length > 0
  );

  // Get counts from server stats (reflect search + date filters, not type filter)
  const counts = {
    complaints: stats.complaint_count,
    maintenance: stats.maintenance_count
  };

  // Get hours breakdown from server
  const hoursData = {
    totalHours: stats.total_hours || 0,
    complaintHours: stats.complaint_hours || 0,
    maintenanceHours: stats.maintenance_hours || 0
  };

  if (loading) {
    return (
      <div className="work-logs-container">
        <div className="work-logs-loading">
          <div className="spinner"></div>
          <p>Loading work logs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="work-logs-container">
        <div className="work-logs-error">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
          <button onClick={fetchWorkLogs} className="btn-retry">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Results are already sorted and paginated by server
  const displayLogs = workLogs;

  return (
    <div className="work-logs-container">
      {/* Title / description — matches Maintenance History */}
      <div className="work-logs-description-section">
        <div className="title-with-button">
          <h2 className="work-logs-title">📋 My Work Logs</h2>
          <button onClick={fetchWorkLogs} className="btn-refresh" title="Refresh work logs" style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer', lineHeight: 1 }}>
            🔄
          </button>
        </div>
        <p className="work-logs-subtitle">View all your maintenance tasks and resolved complaints</p>
      </div>

      {isServerDateWindowActive && (
        <div className="list-default-window-note" role="note">
          <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
          <span>
            Showing work from the{' '}
            <strong>last {MAINTENANCE_LIST_DEFAULT_LOOKBACK_DAYS} days</strong> by default.
            Use Last 7d / 15d / 30d or Advanced Filters for a different range.
          </span>
        </div>
      )}

      {/* Stats below the horizontal bar */}
      <div className="work-logs-header">
        <div className="work-logs-stats">
          <StatsCard
            icon="💬"
            number={counts.complaints}
            label="Complaints"
            className="complaint"
          />
          <StatsCard
            icon="🔧"
            number={counts.maintenance}
            label="Maintenance"
            className="maintenance"
          />
          <StatsCard
            icon="⏱️"
            number={formatHoursSpent(hoursData.totalHours, { zeroDisplay: '0:00' })}
            label="Total Hours"
            className="hours"
            aside={(
              <>
                <div className="stats-card-hours-aside-row">
                  <span aria-hidden="true">💬</span>
                  <strong>{formatHoursSpent(hoursData.complaintHours, { zeroDisplay: '0:00' })}</strong>
                </div>
                <div className="stats-card-hours-aside-row">
                  <span aria-hidden="true">🔧</span>
                  <strong>{formatHoursSpent(hoursData.maintenanceHours, { zeroDisplay: '0:00' })}</strong>
                </div>
              </>
            )}
          />
        </div>
      </div>

      {/* Search — shared primitives from filters-ui.css (matches Maintenance History) */}
      <div className="search-section">
        <div className="search-box-wrapper">
          <span className="search-icon-lens" aria-hidden="true">🔍</span>
          <input
            type="text"
            placeholder="Search by complaint ID, device ID, device name, or description..."
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
            className="search-input-clean"
          />
          {searchInputValue ? (
            <button
              type="button"
              className="clear-search-btn-inline"
              onClick={handleClearSearch}
              title="Clear search"
            >
              ✕
            </button>
          ) : null}
        </div>
      </div>

      {/* Quick Filters — matches Maintenance History presets */}
      <div className="quick-filters">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.75rem' }}>
          <div className="quick-filters-title">
            <svg className="filters-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Quick Filters
          </div>
        </div>
        <div className="preset-buttons">
          <button
            type="button"
            className={`preset-btn ${!hasQuickFiltersActive ? 'active' : ''}`}
            onClick={clearAllFilters}
            title={`Clear quick filters (default last ${MAINTENANCE_LIST_DEFAULT_LOOKBACK_DAYS} days still applies)`}
          >
            All
          </button>
          <button
            type="button"
            className={`preset-btn ${isLastNDaysActive(7) ? 'active' : ''}`}
            onClick={() => toggleLastNDays(7)}
          >
            Last 7d
          </button>
          <button
            type="button"
            className={`preset-btn ${isLastNDaysActive(15) ? 'active' : ''}`}
            onClick={() => toggleLastNDays(15)}
          >
            Last 15d
          </button>
          <button
            type="button"
            className={`preset-btn ${isLastNDaysActive(30) ? 'active' : ''}`}
            onClick={() => toggleLastNDays(30)}
          >
            Last 30d
          </button>
          <button
            type="button"
            className={`preset-btn ${filters.typeFilter === 'maintenance' ? 'active' : ''}`}
            onClick={() => toggleTypeFilter('maintenance')}
            title="Show only maintenance records"
          >
            🔧 Maintenance Only
          </button>
          <button
            type="button"
            className={`preset-btn ${filters.typeFilter === 'complaint' ? 'active' : ''}`}
            onClick={() => toggleTypeFilter('complaint')}
            title="Show only closed complaint records"
          >
            💬 Complaints Only
          </button>
          <MoreFiltersButton
            isOpen={showAdvancedPanel}
            hasAdditional={[
              filters.departmentFilters.length,
              filters.maintenanceTypeFilters.length,
              filters.complaintStatusFilters.length,
              filters.severityFilters.length,
              (filters.dateFrom || filters.dateTo)
                && !isLastNDaysActive(7)
                && !isLastNDaysActive(15)
                && !isLastNDaysActive(30),
            ].some(Boolean)}
            onClick={() => (showAdvancedPanel ? setShowAdvancedPanel(false) : openAdvancedPanel())}
          />
        </div>
      </div>

      {hasQuickFiltersActive && (
        <div className="active-filters">
          <div className="active-filters-header">
            <span className="active-filters-label">Filters Active:</span>
            <button type="button" className="clear-all-btn" onClick={clearAllFilters}>
              Clear filters
            </button>
          </div>
          <div className="active-filters-pills">
            {(filters.dateFrom || filters.dateTo) && (
              <span className="filter-pill">
                Updated
                {filters.dateFrom ? ` from ${formatDate(filters.dateFrom)}` : ''}
                {filters.dateTo ? ` to ${formatDate(filters.dateTo)}` : ''}
                <button
                  type="button"
                  className="filter-pill-remove"
                  onClick={() => applyFilters({ ...filters, dateFrom: '', dateTo: '' })}
                  aria-label="Remove date filter"
                >
                  ✕
                </button>
              </span>
            )}
            {filters.typeFilter === 'maintenance' && (
              <span className="filter-pill">
                Maintenance only
                <button
                  type="button"
                  className="filter-pill-remove"
                  onClick={() => applyFilters({ ...filters, typeFilter: '' })}
                  aria-label="Remove maintenance filter"
                >
                  ✕
                </button>
              </span>
            )}
            {filters.typeFilter === 'complaint' && (
              <span className="filter-pill">
                Complaints only
                <button
                  type="button"
                  className="filter-pill-remove"
                  onClick={() => applyFilters({ ...filters, typeFilter: '' })}
                  aria-label="Remove complaints filter"
                >
                  ✕
                </button>
              </span>
            )}
            {filters.departmentFilters.length > 0 && (
              <span className="filter-pill">
                Department: {filters.departmentFilters.join(', ')}
                <button
                  type="button"
                  className="filter-pill-remove"
                  onClick={() => applyFilters({ ...filters, departmentFilters: [] })}
                  aria-label="Remove department filter"
                >
                  ✕
                </button>
              </span>
            )}
            {filters.maintenanceTypeFilters.length > 0 && (
              <span className="filter-pill">
                Maintenance type: {filters.maintenanceTypeFilters.join(', ')}
                <button
                  type="button"
                  className="filter-pill-remove"
                  onClick={() => applyFilters({ ...filters, maintenanceTypeFilters: [], typeFilter: '' })}
                  aria-label="Remove maintenance type filter"
                >
                  ✕
                </button>
              </span>
            )}
            {filters.complaintStatusFilters.length > 0 && (
              <span className="filter-pill">
                Status: {filters.complaintStatusFilters.join(', ')}
                <button
                  type="button"
                  className="filter-pill-remove"
                  onClick={() => applyFilters({ ...filters, complaintStatusFilters: [], typeFilter: '' })}
                  aria-label="Remove complaint status filter"
                >
                  ✕
                </button>
              </span>
            )}
            {filters.severityFilters.length > 0 && (
              <span className="filter-pill">
                Severity: {filters.severityFilters.join(', ')}
                <button
                  type="button"
                  className="filter-pill-remove"
                  onClick={() => applyFilters({ ...filters, severityFilters: [], typeFilter: '' })}
                  aria-label="Remove severity filter"
                >
                  ✕
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      {showAdvancedPanel && ReactDOM.createPortal(
        <div className="sidebar-drawer-overlay" onClick={() => setShowAdvancedPanel(false)}>
          <div className="sidebar-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-title">
              <span>🔧 Advanced Filters</span>
              <button
                type="button"
                className="drawer-close-btn"
                onClick={() => setShowAdvancedPanel(false)}
                title="Close filters"
              >
                ✕
              </button>
            </div>

            <div style={{
              margin: '0.75rem 1rem',
              padding: '0.6rem 0.8rem',
              background: '#fef3c7',
              border: '1px solid #fcd34d',
              borderRadius: '6px',
              fontSize: '0.8rem',
              color: '#92400e',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.5rem'
            }}>
              <span style={{ fontSize: '1rem', flexShrink: 0 }}>⚠️</span>
              <div>
                <strong>Choose either Maintenance Type or Complaint filters (Status/Severity).</strong>{' '}
                {ADVANCED_FILTER_HINTS.mutualExclusivityBanner}
              </div>
            </div>

            <div className="drawer-filter-content">
              <div className="filter-group">
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedSections((prev) => ({ ...prev, dateRange: !prev.dateRange }))}
                >
                  <span className={`group-arrow ${expandedSections.dateRange ? 'open' : ''}`}>▼</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>📅</span>
                    <span>Updated Date Range</span>
                  </span>
                </div>
                {expandedSections.dateRange && (
                  <div className="filter-group-content">
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">From</div>
                      <input
                        type="date"
                        className="filter-select"
                        style={{ padding: '0.5rem 0.75rem' }}
                        value={drawerFilters.dateFrom || ''}
                        onChange={(e) => setDrawerFilters({ ...drawerFilters, dateFrom: e.target.value })}
                      />
                    </div>
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">To</div>
                      <input
                        type="date"
                        className="filter-select"
                        style={{ padding: '0.5rem 0.75rem' }}
                        value={drawerFilters.dateTo || ''}
                        onChange={(e) => setDrawerFilters({ ...drawerFilters, dateTo: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="filter-group">
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedSections((prev) => ({ ...prev, department: !prev.department }))}
                >
                  <span className={`group-arrow ${expandedSections.department ? 'open' : ''}`}>▼</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>🏢</span>
                    <span>Department</span>
                    {isDepartmentFilterRestricted && (
                      <FilterTitleHint>{ADVANCED_FILTER_HINTS.departmentRestricted}</FilterTitleHint>
                    )}
                  </span>
                </div>
                {expandedSections.department && (
                  <div className="filter-group-content">
                    <div className="filter-item-wrapper full-width">
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.departmentFilters || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                          setDrawerFilters({ ...drawerFilters, departmentFilters: selected });
                        }}
                        style={{ minHeight: '80px' }}
                        disabled={isDepartmentFilterRestricted}
                        title={isDepartmentFilterRestricted
                          ? 'Only your assigned department is available'
                          : 'Hold Ctrl/Cmd to select multiple departments'}
                      >
                        {assignedDepartments.length > 0 ? (
                          assignedDepartments.map((dept) => (
                            <option key={dept.id || dept.name} value={dept.name}>
                              {dept.icon || '🏢'} {dept.name}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>No departments assigned</option>
                        )}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div
                className="filter-group"
                style={{ opacity: drawerHasComplaintFilters ? 0.5 : 1 }}
              >
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedSections((prev) => ({ ...prev, maintenanceType: !prev.maintenanceType }))}
                >
                  <span className={`group-arrow ${expandedSections.maintenanceType ? 'open' : ''}`}>▼</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>🔧</span>
                    <span>Maintenance Type</span>
                    {drawerHasComplaintFilters && (
                      <FilterTitleHint>{ADVANCED_FILTER_HINTS.maintenanceTypeDisabled}</FilterTitleHint>
                    )}
                  </span>
                </div>
                {expandedSections.maintenanceType && (
                  <div className="filter-group-content">
                    <div className="filter-item-wrapper full-width">
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.maintenanceTypeFilters || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                          setDrawerFilters({ ...drawerFilters, maintenanceTypeFilters: selected });
                        }}
                        style={{ minHeight: '100px' }}
                        disabled={drawerHasComplaintFilters}
                        title="Hold Ctrl/Cmd to select multiple types"
                      >
                        <option value="PPM">PPM</option>
                        <option value="Calibration">Calibration</option>
                        <option value="Compliance">Compliance</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div
                className="filter-group"
                style={{ opacity: drawerHasMaintenanceTypeFilters ? 0.5 : 1 }}
              >
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedSections((prev) => ({ ...prev, complaintFilters: !prev.complaintFilters }))}
                >
                  <span className={`group-arrow ${expandedSections.complaintFilters ? 'open' : ''}`}>▼</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>💬</span>
                    <span>Complaint Filters</span>
                    {drawerHasMaintenanceTypeFilters && (
                      <FilterTitleHint>{ADVANCED_FILTER_HINTS.complaintFiltersDisabled}</FilterTitleHint>
                    )}
                  </span>
                </div>
                {expandedSections.complaintFilters && (
                  <div className="filter-group-content complaint-filters-two-col">
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Status</div>
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.complaintStatusFilters || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                          setDrawerFilters({ ...drawerFilters, complaintStatusFilters: selected });
                        }}
                        style={{ minHeight: '80px', width: '100%' }}
                        disabled={drawerHasMaintenanceTypeFilters}
                        title="Hold Ctrl/Cmd to select multiple statuses"
                      >
                        <option value="closed">Closed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Severity</div>
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.severityFilters || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                          setDrawerFilters({ ...drawerFilters, severityFilters: selected });
                        }}
                        style={{ minHeight: '80px', width: '100%' }}
                        disabled={drawerHasMaintenanceTypeFilters}
                        title="Hold Ctrl/Cmd to select multiple severities"
                      >
                        <option value="critical">Critical</option>
                        <option value="major">Major</option>
                        <option value="minor">Minor</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="drawer-buttons">
              <button type="button" className="cancel-btn" onClick={() => setShowAdvancedPanel(false)}>
                Close
              </button>
              <button
                type="button"
                className="reset-btn"
                onClick={() => setDrawerFilters({ ...EMPTY_FILTERS })}
              >
                Reset
              </button>
              <button type="button" className="apply-btn" onClick={applyDrawerFilters}>
                Apply Filters
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ExportColumnConfigurationModal
        isOpen={showExportColumnsConfig}
        onClose={() => setShowExportColumnsConfig(false)}
        title="Configure Export Columns"
        description="A minimal operational set is pre-selected. Add or remove columns as needed. Excel supports any number of columns; PDF is limited for readable landscape layout."
        columns={exportColumns}
        onColumnsChange={setExportColumns}
        pdfMaxColumns={PDF_EXPORT_MAX_COLUMNS}
      />

      {/* Results */}
      {totalRecords === 0 ? (
        <div className="empty-state">
          <p className="empty-icon">📭</p>
          <p className="empty-title">No work logs found</p>
          <p className="empty-subtitle">Try adjusting your filters or search terms</p>
        </div>
      ) : (
        <>
          <div className="work-logs-results-info">
            Showing {displayLogs.length} of {totalRecords} records
            {totalRecords > 0 && (
              <span> — Export includes all {totalRecords} filtered records</span>
            )}
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            paddingBottom: '1rem',
            marginBottom: '1rem',
            borderBottom: '2px solid #e5e7eb'
          }}>
            <ExportButton
              onExportPDF={exportToPDF}
              onExportExcel={exportToExcel}
              title="Export work logs"
              disabled={isExporting}
            />
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                padding: '0.25rem',
                cursor: 'pointer',
                fontSize: '1.5rem',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1'
              }}
              onClick={() => setShowExportColumnsConfig(true)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title="Configure export columns"
              disabled={isExporting}
            >
              ⚙️
            </button>
          </div>

          <div className="work-logs-table-wrapper">
            {displayLogs.length === 0 ? (
              <div className="empty-state" style={{ marginBottom: '1rem' }}>
                <p className="empty-title">No records on this page</p>
                <p className="empty-subtitle">Try going back to the first page</p>
              </div>
            ) : (
            <table className="work-logs-table">
              <thead>
                <tr>
                  <th
                    className="col-type"
                    onClick={() => handleSortChange('type')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by type"
                  >
                    Type{getSortIndicator('type')}
                  </th>
                  <th
                    className="col-title"
                    onClick={() => handleSortChange('title')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by work"
                  >
                    Work{getSortIndicator('title')}
                  </th>
                  <th
                    className="col-device"
                    onClick={() => handleSortChange('device_name')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by device"
                  >
                    Device{getSortIndicator('device_name')}
                  </th>
                  <th
                    className="col-department"
                    onClick={() => handleSortChange('department')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by department"
                  >
                    Department{getSortIndicator('department')}
                  </th>
                  <th
                    className="col-date"
                    onClick={() => handleSortChange('date')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by updated date"
                  >
                    Updated{getSortIndicator('date')}
                  </th>
                  <th
                    className="col-hours"
                    onClick={() => handleSortChange('hours_spent')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Hours spent (hh:mm) — click to sort"
                  >
                    <span className="work-logs-th-stacked">
                      <span className="work-logs-th-label">
                        Hours{getSortIndicator('hours_spent')}
                      </span>
                      <span className="work-logs-th-hint">hh:mm</span>
                    </span>
                  </th>
                  <th className="col-expand" aria-label="Expand details" />
                </tr>
              </thead>
              <tbody>
                {displayLogs.map((log) => {
                  const isExpanded = expandedRowId === log.id;
                  return (
                  <React.Fragment key={log.id}>
                    <tr
                      className={`work-log-row work-log-${log.type}${isExpanded ? ' is-expanded' : ''}`}
                      onClick={() => setExpandedRowId(isExpanded ? null : log.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpandedRowId(isExpanded ? null : log.id);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Hide details' : 'Show details'}
                      title={isExpanded ? 'Hide details' : 'Show details'}
                    >
                      <td className="col-type">
                        <div className="type-simple">
                          <span className="type-icon">{log.icon}</span>
                          <span className="type-text">
                            {log.type === 'complaint' ? 'Complaint' : log.maintenance_type || 'Maintenance'}
                          </span>
                        </div>
                      </td>
                      <td className="col-title">
                        <div className="title-content">
                          {log.type === 'complaint' ? (
                            <>
                              <span className="title-text">{log.title}</span>
                              <ComplaintIDBadge complaintId={log.complaint_id} size="small" />
                            </>
                          ) : (
                            <span className="title-text">{log.title}</span>
                          )}
                        </div>
                      </td>
                      <td className="col-device">
                        <div className="device-info">
                          <span className="device-name">{log.device_name}</span>
                          <DeviceIDBadge deviceId={log.device_id} size="small" />
                        </div>
                      </td>
                      <td className="col-department">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '1rem' }}>{log.department_icon || '🏢'}</span>
                          <span>{log.department || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="col-date">
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <div style={{ fontWeight: '500', fontSize: '0.9rem', color: '#2c3e50' }}>
                            {formatDateTime(log.date).date}
                          </div>
                          <div style={{ fontWeight: '500', fontSize: '0.8rem', color: '#6c757d' }}>
                            {formatDateTime(log.date).time}
                          </div>
                        </div>
                      </td>
                      <td className="col-hours">
                        <span className="hours-badge">{formatHoursSpent(log.hours_spent)}</span>
                      </td>
                      <td className="col-expand">
                        <span
                          className={`work-log-expand-chevron${isExpanded ? ' is-expanded' : ''}`}
                          aria-hidden="true"
                        />
                      </td>
                    </tr>

                    {/* Expanded Details Row */}
                    {isExpanded && (
                      <tr className="work-log-details-row">
                        <td colSpan="7">
                          <div className="details-container">
                            {log.type === 'complaint' ? (
                              <>
                                <div className="details-body">
                                  <div className="details-field">
                                    <span className="details-label">Details</span>
                                    <ClampedDetailsText text={log.problem_details} />
                                  </div>
                                  {log.closing_remarks && (
                                    <div className="details-field">
                                      <span className="details-label">Resolution</span>
                                      <ClampedDetailsText text={log.closing_remarks} />
                                    </div>
                                  )}
                                </div>
                                <div className="details-facts">
                                  <div className="details-fact">
                                    <span className="details-fact-label">Spare parts</span>
                                    <span className={`details-fact-value ${log.spare_parts_required ? 'is-yes' : 'is-no'}`}>
                                      {log.spare_parts_required ? 'Yes' : 'No'}
                                    </span>
                                  </div>
                                  {log.severity && (
                                    <div className="details-fact">
                                      <span className="details-fact-label">Severity</span>
                                      <SeverityBadge severity={log.severity} size="small" />
                                    </div>
                                  )}
                                  <div className="details-fact">
                                    <span className="details-fact-label">Resolution time</span>
                                    <span className="details-fact-value">
                                      {calculateResolutionTime(log.created_at, log.closed_at)}
                                    </span>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="details-body">
                                  <div className="details-field details-field--full">
                                    <span className="details-label">Work description</span>
                                    <ClampedDetailsText text={log.description || 'No description'} />
                                  </div>
                                </div>
                                {(log.due_date || log.next_due_date) && (
                                  <div className="details-facts">
                                    {log.due_date && (
                                      <div className="details-fact">
                                        <span className="details-fact-label">Due (SLA)</span>
                                        <span className="details-fact-value">{formatDate(log.due_date)}</span>
                                      </div>
                                    )}
                                    {log.due_date && (() => {
                                      const timeliness = getMaintenanceTimeliness(log.maintenance_date, log.due_date);
                                      const statusClass = timeliness.isOnTime === null
                                        ? 'is-na'
                                        : (timeliness.isOnTime ? 'is-on-time' : 'is-late');
                                      const statusText = timeliness.isOnTime === null
                                        ? 'N/A'
                                        : (timeliness.isOnTime
                                          ? 'On-time'
                                          : `${timeliness.daysLate} day${timeliness.daysLate === 1 ? '' : 's'} late`);
                                      return (
                                        <div className="details-fact">
                                          <span className="details-fact-label">Completion</span>
                                          <span className={`details-fact-value ${statusClass}`}>{statusText}</span>
                                        </div>
                                      );
                                    })()}
                                    {log.next_due_date && (
                                      <div className="details-fact">
                                        <span className="details-fact-label">Next due</span>
                                        <span className="details-fact-value">{formatDate(log.next_due_date)}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                            <div className="details-meta">
                              <span className="meta-item">
                                Logged {formatDateTime(log.logDate).date} · {formatDateTime(log.logDate).time}
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            )}

          {/* Pagination — keep visible when records exist so per-page selector stays available */}
          {totalRecords > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                setCurrentPage(page);
                setExpandedRowId(null);
              }}
              perPage={perPage}
              onPerPageChange={(newPerPage) => {
                setPerPage(newPerPage);
                setCurrentPage(1);
                setExpandedRowId(null);
              }}
              totalItems={totalRecords}
              itemLabel="records"
            />
          )}
          </div>
        </>
      )}
    </div>
  );
};

export default MyWorkLogs;
